import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VaultService } from '../../../../core/services/quiz/vault.service';
import { AppStateService } from '../../../../core/services/quiz/app-state.service';
import { UserService } from '../../../../core/services/quiz/user.service';
import { Poster } from '../../../../core/models';

@Component({
  selector: 'app-vault',
  standalone: true,
  imports: [CommonModule],
  template: `
<div class="page-wrapper">
  <div class="vault-container">

    <!-- Header -->
    <div class="vault-header anim-fade-up">
      <p class="eyebrow-gold">✦ Collectibles & Archival Vault ✦</p>
      <h1 class="vault-title">The Vault</h1>
      <p class="vault-sub">{{ vault?.unlocked || 0 }} / {{ vault?.total || 0 }} Collectibles Unlocked</p>
      <p class="vault-copy">Unlock collectible vintage film posters and cinema milestones using your earned tickets.</p>
    </div>

    <!-- Filter tabs -->
    <div class="filter-tabs anim-fade-up" style="animation-delay:.1s">
      <button *ngFor="let f of filters" class="filter-btn" [class.active]="activeFilter === f" (click)="activeFilter = f">
        {{ f }}
      </button>
    </div>

    <!-- Tickets balance -->
    <div class="balance-bar anim-fade-up" style="animation-delay:.15s">
      <span class="eyebrow">Your Box Office Balance:</span>
      <span class="balance-num">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:middle;margin-right:6px">
          <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
          <path d="M13 5v2"/>
          <path d="M13 11v2"/>
          <path d="M13 17v2"/>
        </svg>
        {{ tickets | number }} tickets
      </span>
    </div>

    <!-- Loading -->
    <div class="loading-screen" *ngIf="loading"><div class="loader-ring"></div></div>

    <!-- Error -->
    <div class="error-state" *ngIf="!loading && error"><p>{{ error }}</p></div>

    <!-- Grid -->
    <div class="poster-grid anim-fade-in" *ngIf="!loading && !error">
      <div
        class="poster-card"
        *ngFor="let p of filteredPosters"
        [class.unlocked]="p.is_unlocked"
        [class.locked]="!p.is_unlocked">

        <!-- Background / Image -->
        <div class="pc-bg" [style.background]="p.bg_gradient">
          <img *ngIf="p.image_url" [src]="p.image_url" [alt]="p.title" class="pc-img" (error)="$any($event.target).style.display='none'" />
        </div>
        <div class="pc-overlay"></div>
        <div class="pc-glow"></div>

        <!-- Lock overlay -->
        <div class="pc-lock" *ngIf="!p.is_unlocked">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <!-- Type badge -->
        <div class="pc-type-badge">{{ (p.item_type || 'poster') | uppercase }}</div>

        <!-- Rarity badge -->
        <div class="pc-rarity" [style.color]="vaultSvc.getRarityColor(p.rarity)">
          {{ vaultSvc.getRarityLabel(p.rarity) }}
        </div>

        <div class="pc-content">
          <div class="pc-title">{{ p.title }}</div>
          <div class="pc-year">{{ p.year ? p.year + ' · ' : '' }}{{ p.genre || 'Cinema' }}</div>

          <!-- Earned badge -->
          <div class="pc-earned" *ngIf="p.is_unlocked">
            <span class="earned-text">✦ Unlocked</span>
            <span *ngIf="p.earned_via" class="earned-via">via {{ p.earned_via === 'ticket_purchase' ? 'tickets' : p.earned_via }}</span>
          </div>

          <!-- Purchase button -->
          <div class="pc-purchase" *ngIf="!p.is_unlocked">
            <div class="pc-cost">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
                <path d="M13 5v2"/><path d="M13 11v2"/><path d="M13 17v2"/>
              </svg>
              <span>{{ p.ticket_cost | number }} tickets</span>
            </div>
            <button
              class="btn-primary"
              style="padding:8px 16px;font-size:10px;border-radius:10px"
              [disabled]="tickets < p.ticket_cost || purchasing === p.poster_id"
              (click)="purchase(p)">
              {{ purchasing === p.poster_id ? 'Unlocking...' : 'Unlock Now' }}
            </button>
          </div>
        </div>

        <!-- Success flash -->
        <div class="pc-success" *ngIf="justUnlocked === p.poster_id">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>Unlocked!</span>
        </div>
      </div>
    </div>

    <div class="empty-vault" *ngIf="!loading && !error && filteredPosters.length === 0">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color:var(--vault-muted);margin:0 auto 12px">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
      <p style="font-style:italic;color:var(--vault-muted)">No collectibles found in this category.</p>
    </div>
  </div>
</div>
  `,
  styleUrls: ['./vault.scss']
})
export class VaultComponent implements OnInit {
  vault: { total: number; unlocked: number; locked: number; posters: Poster[] } | null = null;
  tickets = 0;
  loading = true;
  error = '';
  purchasing: string | null = null;
  justUnlocked: string | null = null;
  activeFilter = 'All';

  filters = ['All', 'Posters', 'Milestones', 'Unlocked', 'Locked', 'Common', 'Rare', 'Legendary'];

  constructor(
    public vaultSvc: VaultService,
    private userSvc: UserService,
    private state: AppStateService
  ) {}

  ngOnInit(): void {
    this.state.user$.subscribe(u => {
      if (u) {
        this.tickets = u.tickets;
      }
    });
    this.userSvc.loadCurrentUser().subscribe({
      next: (u) => {
        if (u) this.tickets = u.tickets;
      }
    });
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.vaultSvc.getUserVault(this.state.userId).subscribe({
      next: (v) => { this.vault = v; this.loading = false; },
      error: () => { this.error = 'Could not load vault.'; this.loading = false; }
    });
  }

  get filteredPosters(): Poster[] {
    if (!this.vault) return [];
    const p = this.vault.posters;
    switch (this.activeFilter) {
      case 'Posters':   return p.filter(x => (x.item_type || 'poster') === 'poster');
      case 'Milestones':return p.filter(x => x.item_type === 'milestone');
      case 'Unlocked':  return p.filter(x => x.is_unlocked);
      case 'Locked':    return p.filter(x => !x.is_unlocked);
      case 'Common':    return p.filter(x => x.rarity === 'common');
      case 'Rare':      return p.filter(x => x.rarity === 'rare');
      case 'Legendary': return p.filter(x => x.rarity === 'legendary');
      default:          return p;
    }
  }

  purchase(poster: Poster): void {
    this.purchasing = poster.poster_id;
    this.vaultSvc.purchasePoster(poster.poster_id).subscribe({
      next: (res) => {
        this.purchasing = null;
        if (res.message === 'Purchase failed') {
          alert('Purchase failed — not enough tickets or already unlocked.');
          return;
        }
        this.justUnlocked = poster.poster_id;
        this.tickets = res.new_balance;
        if (this.vault) {
          const idx = this.vault.posters.findIndex(p => p.poster_id === poster.poster_id);
          if (idx >= 0) {
            this.vault.posters[idx] = { ...this.vault.posters[idx], is_unlocked: true, earned_via: 'ticket_purchase' };
            this.vault.unlocked++;
            this.vault.locked--;
          }
        }
        setTimeout(() => this.justUnlocked = null, 2500);
      },
      error: (err) => {
        this.purchasing = null;
        alert(err?.error?.detail || 'Purchase failed — check your ticket balance.');
      }
    });
  }
}
